/**
 * Google Ads tag for the author app — the conversion side of the pipeline.
 *
 * The marketing site (quickpoint_website/js/attribution.js) runs the same
 * AW- tag post-consent, which stores the click id in Google's _gcl_* cookies
 * on .quickpoint.me, and also carries gclid onto links into this app. Either
 * path lets the tag here attribute a signup conversion back to the ad click.
 *
 * Consent Mode v2: the tag itself loads UNCONDITIONALLY (Google Ads refuses
 * to finish conversion-action setup unless its crawler — which never grants
 * consent — can detect the tag), but every consent signal defaults to
 * 'denied' unless the shared qp_consent cookie says granted. Denied mode
 * stores no cookies and sends only cookieless pings — Google's own
 * recommended pattern for consent-gated sites. The conversion EVENT stays
 * hard-gated on consent in fireGoogleSignupConversion, matching the rest of
 * conversionTracking.ts.
 */

declare global {
	interface Window {
		dataLayer?: unknown[];
		gtag?: (...args: unknown[]) => void;
	}
}

const GOOGLE_ADS_ID = "AW-18465381933";

// Google Ads conversion action "Sign-up" — fires on real account creation
// only (not marketing-site CTA clicks, which would dilute the signal).
const SIGNUP_CONVERSION_LABEL: string | null = "ANgJCO2_x5UdEK28_eRE";

const hasAdConsent = (): boolean =>
	document.cookie.match(/(?:^|; )qp_consent=([^;]*)/)?.[1] === "granted";

/** Call early in the app lifecycle, alongside initConversionTracking. */
export const loadGoogleTag = (): void => {
	if (window.gtag) return;
	window.dataLayer = window.dataLayer || [];
	// MUST push the `arguments` object, not a real array — gtag.js silently
	// ignores plain-array commands (they're the GTM dataLayer dialect).
	window.gtag = function gtag() {
		// eslint-disable-next-line prefer-rest-params
		window.dataLayer?.push(arguments);
	};
	const state = hasAdConsent() ? "granted" : "denied";
	window.gtag("consent", "default", {
		ad_storage: state,
		ad_user_data: state,
		ad_personalization: state,
		analytics_storage: state,
	});
	// keep an incoming gclid alive across SPA navigation pre-consent
	window.gtag("set", "url_passthrough", true);
	window.gtag("js", new Date());
	window.gtag("config", GOOGLE_ADS_ID);
	const s = document.createElement("script");
	s.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`;
	s.async = true;
	document.head.appendChild(s);
};

/**
 * Fire the Google Ads signup conversion. gtag commands queue into dataLayer,
 * so this is safe even while gtag.js itself is still downloading. Hard-gated
 * on ad consent regardless of the tag being loaded.
 */
export const fireGoogleSignupConversion = (): void => {
	if (!SIGNUP_CONVERSION_LABEL || !window.gtag || !hasAdConsent()) return;
	// consent may have been granted (marketing-site banner, other tab) after
	// the tag loaded with denied defaults — lift the signals before the event
	window.gtag("consent", "update", {
		ad_storage: "granted",
		ad_user_data: "granted",
		ad_personalization: "granted",
		analytics_storage: "granted",
	});
	window.gtag("event", "conversion", {
		send_to: `${GOOGLE_ADS_ID}/${SIGNUP_CONVERSION_LABEL}`,
	});
};
